import Big from 'big.js';
import { Tooltip } from 'bootstrap';
import { GetChildPrice, GetOptionPrice, GetPrice, GetPrices, GetSizes, GetTypes, Size } from 'smg/customer-api/lineitems';
import * as quoterequests from 'smg/customer-api/quoterequests';
import { CleanNumber } from 'smg/number';
import * as object from 'smg/object';
import { G } from 'smg/querystring';
import * as strings from 'smg/strings';
import * as template from 'smg/template';
import { DollarFormat } from 'smg/util';
import { GetQuizRequest } from '../components/product-type-quiz';
import { GetMoldFee } from '../moldfees';
import { QuoteModal } from '../quote';

declare interface addlUnitCost {
    child: string;
    option?: string;
}

interface myWindow extends Window {
    readonly AddlUnitCosts?: string;
}
declare const window: myWindow;

function isAddlUnitCosts(obj: unknown): obj is addlUnitCost[] {
    if (!Array.isArray(obj)) {
        return false;
    }

    for (const item of obj) {
        if (typeof item !== 'object' || !('child' in item)) {
            return false;
        }

        if (!(object.IsObject(item) && (!object.HasKey(item, 'option') || typeof item.option === 'string'))) {
            return false;
        }
    }

    return true;
}

let addlUnitCostsArray: addlUnitCost[];

function addlUnitCosts() {
    if (typeof addlUnitCostsArray === 'undefined') {
        let addlUnitCosts: addlUnitCost[] = [];

        if (window.AddlUnitCosts && typeof window.AddlUnitCosts === 'string') {
            const costs: unknown = JSON.parse(window.AddlUnitCosts);
            if (isAddlUnitCosts(costs)) {
                addlUnitCosts = costs;
            }
        }

        addlUnitCostsArray = addlUnitCosts;
    }

    return addlUnitCostsArray;
}

let totalAddlUnitCostsBig: Big | undefined;

async function totalAddlUnitCosts(product: string): Promise<Big> {
    if (typeof totalAddlUnitCostsBig === 'undefined') {
        let total = Big(0);

        for (const addlUnitCost of addlUnitCosts()) {
            if (addlUnitCost.option) {
                total = total.plus(await GetOptionPrice(product, addlUnitCost.child, addlUnitCost.option));
            } else {
                total = total.plus(await GetChildPrice(product, addlUnitCost.child));
            }
        }

        totalAddlUnitCostsBig = total;
    }

    return totalAddlUnitCostsBig;
}

if (document.body.id === 'product-detail') {
    void main();
}

function displaySize(size: Size): string {
    if (size.name) {
        return size.name;
    }

    let s = '';

    if (size.width) {
        s += `${strings.Trim(String(size.width), '0')} inch`;

        if (size.height && size.width !== size.height) {
            s += ' × ';
        }
    }

    if (size.height && size.width !== size.height) {
        s += `${strings.Trim(String(size.height), '0')} inch`;
    }

    if (size.type) {
        s += ` ${size.type}`;
    }

    return s;
}

type productName = 'Lapel Pins' | 'Challenge Coins' | 'Patches' | 'Lanyards' | 'Belt Buckles' | 'Key Chains' | 'Keychains' | 'Medals';

function shortProductSingular(product: productName): string {
    switch (product) {
    case 'Lapel Pins':
        return 'Pin';
    case 'Challenge Coins':
        return 'Coin';
    case 'Patches':
        return 'Patch';
    case 'Lanyards':
        return 'Lanyard';
    case 'Belt Buckles':
        return 'Buckle';
    case 'Key Chains':
    case 'Keychains':
        return 'Keychain';
    case 'Medals':
        return 'Medal';
    default:
        return product;
    }
}

async function main() {
    const $mainImageContainer = document.querySelector('#main-image-container') as HTMLElement;
    const $sliderImages = Array.from(document.querySelectorAll<HTMLElement>('[data-slider-image-id]'));
    $sliderImages.forEach($sliderImage => {
        $sliderImage.addEventListener('mouseover', () => {
            const $mainImage = document.querySelector<HTMLElement>(`#${$sliderImage.getAttribute('data-slider-image-id') || ''}`) as HTMLElement;
            for (const $child of Array.from($mainImageContainer.children)) {
                $child.classList.add('d-none');
            }
            $mainImage.classList.remove('d-none');
        });
    });

    const $sizeAndQuantity = document.querySelector('#quote-size-and-quantity') as HTMLElement;
    const $collapse = $sizeAndQuantity.closest('.collapse') as HTMLElement;
    const product = $collapse.dataset.product;
    const typeName = $collapse.dataset.type;

    const $sizeTemplate = document.querySelector('#quote-size-radio-template') as HTMLTemplateElement;
    const $sizesParent = document.querySelector('#quote-sizes-parent') as HTMLElement;

    const type = (await GetTypes(product ?? '')).find(t => t.name === typeName);
    let currSize: Size | undefined;

    const sizes = await GetSizes(product ?? '', typeName ?? '');
    let $firstSize: HTMLInputElement | null = null;
    let sizeSelected = false;

    let allowedSizes: Set<string> | undefined;
    if ($sizeAndQuantity.dataset.sizes) {
        allowedSizes = new Set($sizeAndQuantity.dataset.sizes.split(';'));
    }

    const $moldFeeEls = document.querySelectorAll<HTMLElement>('.mold-fee');

    if ($moldFeeEls.length) {
        const $tooltipContent = template.Clone('#tooltip-content-template').firstElementChild as HTMLElement;
        const $moldFeeTooltip = document.querySelector('.mold-fee-tooltip') as HTMLElement;
        const $closeBtn = $tooltipContent.querySelector('.close-btn') as HTMLElement;

        const getWindowSize = (): [number, number] => {
            if (window.innerWidth >= 768) {
                return [-250, 5];
            } else {
                const ms = (window.innerWidth - 513) / 2;
                return [-ms, 5];
            }
        };

        const $t = new Tooltip($moldFeeTooltip, {
            trigger: 'click',
            placement: 'top',
            title: $tooltipContent,
            html: true,
            template: '<div class="tooltip" role="tooltip"><div class="tooltip-arrow d-none"></div><div class="tooltip-inner"></div></div>',
            offset: getWindowSize(),
        });
        $closeBtn.addEventListener('click', () => {
            $t.hide();
        });
    }

    for (const size of sizes) {
        if (!size.prices.length) {
            continue;
        }

        if (allowedSizes && !allowedSizes.has(size.name)) {
            continue;
        }

        const $size = template.Clone($sizeTemplate).firstElementChild as HTMLElement;
        const $radio = $size.querySelector('.form-check-input') as HTMLInputElement;
        $radio.value = size.name;
        $radio.addEventListener('click', () => {
            setTimeout(() => {
                void showQtys();
            });
        });

        if (!$firstSize) {
            $firstSize = $radio;
        }

        if (G().Get('size') === size.name) {
            $radio.checked = true;
            $radio.dispatchEvent(new Event('click'));
            sizeSelected = true;
        }

        const $label = $size.querySelector('.form-check-text') as HTMLElement;
        $label.textContent = displaySize(size);

        $sizesParent.appendChild($size);
    }

    if ($firstSize && !sizeSelected) {
        $firstSize.checked = true;
        $firstSize.dispatchEvent(new Event('click'));
    }

    const getSize = () => {
        const sizeName = $sizesParent.querySelector<HTMLInputElement>('input[name="quote-size"]:checked')?.value ?? '';
        if (!sizeName) {
            currSize = undefined;
            return '';
        }
        const size = sizes.find(s => s.name === sizeName);
        if (size) {
            currSize = size;
        } else {
            currSize = undefined;
        }
        return sizeName;
    };

    const $bulkPricing = document.querySelector('#quote-bulk-pricing-link') as HTMLAnchorElement;
    const href = new URL($bulkPricing.href);

    const updateBulkPricingHref = () => {
        const updatedHref = new URL(href.toString());
        updatedHref.searchParams.set('size', getSize());
        updatedHref.searchParams.set('qty', getQty().toString());

        $bulkPricing.href = updatedHref.toString();
    };

    const $qtyTemplate = document.querySelector('#quote-qty-radio-template') as HTMLTemplateElement;
    const $qtysParent = document.querySelector('#quote-qtys-parent') as HTMLElement;

    const $otherQtyContainer = document.querySelector('#quote-custom-qty-input-container') as HTMLElement;
    const $otherQty = $otherQtyContainer.querySelector('input') as HTMLInputElement;
    const $otherDecrement = $otherQtyContainer.querySelector('.decrement') as HTMLElement;
    const $otherIncrement = $otherQtyContainer.querySelector('.increment') as HTMLElement;

    $otherQty.addEventListener('input', async () => {
        await updateMoldFee();
        void updateTotal();
        updateBulkPricingHref();
        updateTitle();
    });

    $otherDecrement.addEventListener('click', e => {
        e.preventDefault();
        $otherQty.value = String(Math.max(0, parseInt(CleanNumber($otherQty.value), 10) - 1));

        $otherQty.dispatchEvent(new Event('input'));
    });

    $otherIncrement.addEventListener('click', e => {
        e.preventDefault();
        $otherQty.value = String(parseInt(CleanNumber($otherQty.value), 10) + 1);

        $otherQty.dispatchEvent(new Event('input'));
    });

    const appendQty = (qty: number, price: Big, first: boolean, checked: boolean = first, other?: boolean) => {
        const $qty = template.Clone($qtyTemplate).firstElementChild as HTMLElement;
        const $radio = $qty.querySelector('.form-check-input') as HTMLInputElement;
        $radio.value = !other ? String(qty) : 'other';

        const $priceContainer = $qty.querySelector('.quote-qty-price-container') as HTMLElement;
        const $price = $qty.querySelector('.quote-qty-price') as HTMLElement;
        const $product = $qty.querySelector('.quote-qty-product') as HTMLElement;
        const $saveHr = $qty.querySelector('.quote-qty-save-hr') as HTMLElement;
        const $save = $qty.querySelector('.quote-qty-save') as HTMLElement;

        $price.textContent = !price.eq(0) ? DollarFormat(price) : '';
        $product.textContent = shortProductSingular(product as productName).toLowerCase();

        if (other) {
            $priceContainer.remove();
        } else if (first) {
            firstUnitPrice = price;

            $saveHr.classList.add('invisible');
            $save.remove();
        } else if (firstUnitPrice) {
            const percentOff = firstUnitPrice.minus(price).div(firstUnitPrice).times(100).round();
            $save.textContent = `SAVE ${percentOff.toString()}%`;
        }

        const $label = $qty.querySelector('.form-check-text') as HTMLElement;
        $label.textContent = !other ? qty.toLocaleString() : 'Other';

        $radio.addEventListener('click', async () => {
            if (other) {
                $otherQtyContainer.classList.remove('d-none');
                $otherQty.focus();
                $otherQty.select();
            } else {
                $otherQtyContainer.classList.add('d-none');
                $otherQty.value = qty.toString();

                updateTitle();
            }

            await updateMoldFee();
            void updateTotal();
            updateBulkPricingHref();
        });

        $qtysParent.appendChild($qty);

        if (checked) {
            $radio.checked = true;
            $radio.dispatchEvent(new Event('click'));
        }
    };

    function updateTitle() {
        const text = `${getSize()} | ${getQty().toLocaleString()} Qty`;
        const $selectedQuoteValue = document.querySelector('.selected-quote-value') as HTMLElement;
        $selectedQuoteValue.textContent = text;
    }

    function updateMultiSelectTitles($selectedQuoteValue: HTMLElement) {
        const checkedOptions = Array.from(document.querySelectorAll('input[type="checkbox"]:checked')).map(option => (option as HTMLInputElement).value);
        const newTitle = checkedOptions.join(', ');
        $selectedQuoteValue.textContent = newTitle;
    }

    const $spinner = $sizeAndQuantity.querySelector('.spinner-parent') as HTMLElement;
    const $sizeAndQuantityContent = $sizeAndQuantity.querySelector('.step-contents') as HTMLElement;

    let firstUnitPrice: Big | undefined;
    const showQtys = async () => {
        const prevQty = getQty() || parseInt(CleanNumber(G().Get('qty') ?? '0'), 10);
        $qtysParent.innerHTML = '';

        const qtys = await GetPrices(product ?? '', typeName ?? '', 0, undefined, undefined, getSize());
        let prevQtyInSet = false;

        for (let i = 0; i < qtys.length; i++) {
            const qty = qtys[i];

            if (prevQty === qty.quantity) {
                prevQtyInSet = true;
            }

            const addlPrice = await totalAddlUnitCosts(product ?? '');

            appendQty(qty.quantity, qty.price.add(addlPrice), !i, prevQty ? prevQty === qty.quantity : undefined);
        }

        const otherChecked = prevQty ? !prevQtyInSet : false;
        appendQty(0, Big(0), false, otherChecked, true);
        if (otherChecked) {
            $otherQty.value = prevQty.toString();
            $otherQty.dispatchEvent(new Event('input'));
        }

        $spinner.remove();
        $sizeAndQuantityContent.classList.remove('d-none');
    };

    const getQty = () => {
        const $checked = $qtysParent.querySelector<HTMLInputElement>('input[name="quote-qty"]:checked');
        if (!$checked) {
            return 0;
        }
        if ($checked?.value === 'other') {
            return parseInt(CleanNumber($otherQty.value || '0'), 10);
        }
        return parseInt(CleanNumber($checked?.value || '0'), 10);
    };

    const $options = document.querySelectorAll<HTMLInputElement>('.quote-step-option');
    $options.forEach($option => $option.addEventListener('click', () => {
        void updateTotal();
        if ($option.type === 'radio') {
            const $quoteStepParent = $option.closest('.quote-step-parent') as HTMLElement;
            const $selectedQuoteValue = $quoteStepParent.querySelector('.selected-quote-value') as HTMLElement;
            if ($selectedQuoteValue) {
                $selectedQuoteValue.textContent = $option.value;
                $selectedQuoteValue.title = $option.value;
            }
        } else if ($option.type === 'checkbox') {
            const $quoteStepParent = $option.closest('.quote-step-parent') as HTMLElement;
            const $selectedQuoteValue = $quoteStepParent.querySelector('.selected-quote-value') as HTMLElement;

            updateMultiSelectTitles($selectedQuoteValue);
        }
    }));

    const updateMoldFee = async () => {
        if (!$moldFeeEls.length) {
            return;
        }

        const moldFee = await GetMoldFee(product ?? '', type?.id ?? '', currSize?.id ?? '', currSize?.width ?? 0, currSize?.height ?? 0, getQty());
        $moldFeeEls.forEach($moldFee => {
            $moldFee.textContent = DollarFormat(moldFee);
            $moldFee.dataset.price = moldFee.toString();
        });
    };

    const $includedChildren = document.querySelectorAll<HTMLElement>('.included-child');

    const $total = document.querySelectorAll<HTMLElement>('.quote-total');
    const updateTotal = async () => {
        let qty = getQty();
        const minQty = $qtysParent.querySelector<HTMLInputElement>('input.form-check-input');
        qty = Math.max(qty, parseInt(minQty?.value || '0', 10));

        let price = await GetPrice(product ?? '', typeName ?? '', getSize(), qty);
        price = price.plus(await totalAddlUnitCosts(product ?? ''));

        const $prices: HTMLElement[] = [];

        for (const $option of Array.from($options)) {
            if ($option.checked) {
                const $label = $option.closest('label.form-control') as HTMLElement;
                $prices.push(...Array.from($label.querySelectorAll<HTMLElement>('.child-line-item-option-price[data-price],.child-line-item-price[data-price]')));
            }
        }

        $includedChildren.forEach($includedChild => {
            $prices.push($includedChild);
        });

        for (const $price of $prices) {
            if ($price.hasAttribute('data-single')) {
                continue;
            }

            price = price.plus(Big($price.dataset.price ?? ''));
        }

        let total = price.times(qty);

        for (const $price of $prices) {
            if (!$price.hasAttribute('data-single')) {
                continue;
            }

            total = total.plus(Big($price.dataset.price ?? ''));
        }

        document.querySelectorAll<HTMLElement>('.additional-one-time-fee').forEach($fee => {
            total = total.plus(Big($fee.dataset.price ?? ''));
        });

        $moldFeeEls.forEach($moldFee => {
            total = total.plus(Big($moldFee.dataset.price ?? ''));
        });

        $total.forEach($total => {
            $total.textContent = DollarFormat(total);
        });
    };

    void showQtys();

    const $getQuote = document.querySelectorAll<HTMLElement>('.quote-get');
    $getQuote.forEach($getQuote => $getQuote.addEventListener('click', () => {
        const children: quoterequests.Child[] = [];
        const extras: quoterequests.Extra[] = [];

        extras.push({
            name: 'Product Page',
            value: document.body.dataset.title,
        });

        for (const addlUnitCost of addlUnitCosts()) {
            children.push({
                child: addlUnitCost.child,
                option: addlUnitCost.option,
            });
        }

        $includedChildren.forEach($includedChild => {
            children.push({
                child: $includedChild.dataset.child ?? '',
                option: $includedChild.dataset.option ?? '',
            });
        });

        document.querySelectorAll<HTMLElement>('.additional-one-time-fee').forEach($fee => {
            children.push({
                child: $fee.dataset.child ?? '',
                option: $fee.dataset.option ?? '',
            });
        });

        $options.forEach($option => {
            if ($option.checked) {
                const $label = $option.closest('label.form-control') as HTMLElement;
                const $price = $label.querySelectorAll<HTMLElement>('.child-line-item-option-price,.child-line-item-price');
                $price.forEach($price => {
                    let option = $price.dataset.option ?? '';
                    if ($option.classList.contains('skip-option-in-quote')) {
                        option = '';
                    }

                    children.push({
                        child: $price.dataset.child ?? '',
                        option,
                    });
                });
            }
        });

        const quiz = GetQuizRequest();
        let quizNotes = '';
        if (quiz) {
            quizNotes = 'I filled out the quiz and these were my answers:\n\n';
            quizNotes += quiz.questions.map(q => `${q.question}:\n${q.answer}\n\n`).join('');
        }

        QuoteModal({
            lineItem: product ?? '',
            size: getSize(),
            type: typeName ?? '',
            quantity: getQty(),
            children,
            extras,
            prefixNotes: quizNotes,

            form: `product detail (${document.body.dataset.title || document.title})`,
            backButtonText: 'Close',
        });
    }));

    const $quoteActions = document.querySelector('#quote-actions') as HTMLElement;

    const getActionsSpaceBelow = () => {
        const bounds = $quoteActions.getBoundingClientRect();
        return window.innerHeight - bounds.bottom;
    };

    const getPodiumWidget = () => document.querySelector<HTMLElement>('#podium-website-widget');

    const getPodiumHeight = () => {
        let maxTopFromBottom: number | undefined;
        let minBottom: number | undefined;

        document.querySelectorAll<HTMLElement>('#podium-website-widget > iframe').forEach($iframe => {
            const bounds = $iframe.getBoundingClientRect();

            const topFromBottom = bounds.height + bounds.bottom;
            if (maxTopFromBottom === undefined || topFromBottom > maxTopFromBottom) {
                maxTopFromBottom = topFromBottom;
            }

            if (minBottom === undefined || bounds.bottom > minBottom) {
                minBottom = bounds.bottom;
            }
        });

        return (maxTopFromBottom ?? 0) - (minBottom ?? 0);
    };

    let ticking = false;
    const updatePodiumOffset = () => {
        if (!ticking) {
            window.requestAnimationFrame(() => {
                const actionsSpaceBelow = getActionsSpaceBelow();
                const podiumHeight = getPodiumHeight();

                if (actionsSpaceBelow < podiumHeight) {
                    getPodiumWidget()?.classList.add('opacity-0', 'pe-none');
                } else {
                    getPodiumWidget()?.classList.remove('opacity-0', 'pe-none');
                }

                ticking = false;
            });

            ticking = true;
        }
    };

    window.addEventListener('resize', updatePodiumOffset);
    document.body.addEventListener('scroll', updatePodiumOffset);

    updatePodiumOffset();
}
